import { Card, IconButton, Typography, SidePanel, Blockquote, Icon } from "@/components/atoms";
import { BookingDetails } from "@/redux/slices/booking/types";
import { formatCurrency, getAssetUrl } from "@/utils";
import { useSelectedBooking } from "../../hooks/useSelectedBooking";
import { ViewTripDriverActivity } from "./components/ViewTripDriverActivity";
import { closeJobTypes, extraTypeOptions } from "./fixtures";

interface ViewClosingExtrasNotesProps {
  open: boolean;
  onClose: () => void;
}

export const ViewClosingExtrasNotes = ({ open, onClose }: ViewClosingExtrasNotesProps) => {
  const booking = useSelectedBooking();
  const { closeMeta } = booking;

  if (!closeMeta) {
    return null;
  }

  const renderBody = () => {
    switch (closeMeta.reason) {
      case "auto_closed":
        return <AutoClosed />;
      case "closed_no_cost":
        return <ClosedNoCost justification={closeMeta.driverNotes} />;
      default:
        return <ClosingDetails booking={booking} />;
    }
  };

  const title = closeJobTypes.find((type) => type.value === closeMeta.reason)?.name;

  return (
    <SidePanel open={open} onClose={onClose} width={550} focused childContent={<ViewTripDriverActivity open={open} />}>
      <section className="flex items-center justify-between p-5">
        <div className="flex items-center gap-2">
          <Icon name="TickCircle" size="lg" className="text-neutral-dark-gray" />
          <Typography variant="title">Close Job ({booking.id})</Typography>
        </div>
        <div className="ml-3 flex h-7 items-center">
          <IconButton iconName="ArrowRight" onClick={onClose} className="-mr-2" variant="custom" />
        </div>
      </section>
      <section className="flex min-h-full flex-col space-y-4 p-5">
        {title && (
          <Card className="py-3">
            <Typography variant="title">{title}</Typography>
          </Card>
        )}
        {renderBody()}
      </section>
    </SidePanel>
  );
};

const AutoClosed = () => {
  return (
    <div className="flex flex-1 flex-col space-y-5">
      <Card>
        <Typography>This job was auto closed by the system.</Typography>
      </Card>
    </div>
  );
};

const ClosedNoCost = ({ justification }: { justification: string }) => {
  return (
    <div className="flex flex-1 flex-col space-y-5">
      <Card>
        <Typography variant="title">Closed at No Cost</Typography>
      </Card>
      {justification && (
        <Card>
          <Typography>Justification</Typography>
          <Typography>{justification}</Typography>
        </Card>
      )}
    </div>
  );
};

const ExtraCard = ({
  title,
  description,
  cost,
  currency,
  details,
}: {
  title: string;
  description?: string;
  cost?: number;
  currency?: string;
  details?: string | number;
}) => (
  <Card className="space-y-3">
    <div className="space-y-3">
      <Typography variant="title">{title}</Typography>
      {description && (
        <Typography variant="small-regular" className="block text-neutral-dark-gray">
          {description}
        </Typography>
      )}
    </div>
    <div className="flex flex-col gap-3">
      {details && (
        <div className="flex items-center gap-2.5">
          <div className="flex h-5 w-5 items-center justify-center">
            <div className={`h-[7px] w-[7px] rounded-full border border-neutral-dark-gray`} />
          </div>
          <Typography variant="action">{details}</Typography>
        </div>
      )}
      {cost !== undefined && !isNaN(cost) && (
        <div className="flex items-center gap-2.5">
          <Icon name="DollarCircle" size="lg" className="text-neutral-dark-gray" />
          <Typography variant="action">
            {formatCurrency(cost, 2, currency || "AUD", {
              prependCurrencySymbol: true,
              skipDecimalConversion: true,
            })}
          </Typography>
        </div>
      )}
    </div>
  </Card>
);

const ClosingDetails = ({ booking }: { booking: BookingDetails }) => {
  const {
    driver,
    closeMeta,
    cost: { currency },
  } = booking;

  if (!closeMeta) return null;

  const { waitTime, additionalRunTime, unscheduledStops, parkingCharges, tollCharges, otherExtras, providerNotes, clientNotes } = closeMeta;

  return (
    <div className="space-y-4">
      {waitTime && (
        <ExtraCard
          title="Wait Time"
          description="Additional Waiting Time (minutes) after the Courtesy Waiting Times"
          cost={waitTime.cost}
          currency={currency}
          details={`${waitTime.name} ${waitTime.name === "1" ? "minute" : "minutes"}`}
        />
      )}

      {parkingCharges && (
        <ExtraCard
          title="Airport Parking Charges"
          cost={parkingCharges.cost}
          currency={currency}
          details={`${extraTypeOptions.find((option) => option.value === parkingCharges.type)?.name} Payment`}
        />
      )}

      {additionalRunTime && (
        <ExtraCard
          title="Additional Run Time"
          description="Additional Run Time (minutes)"
          cost={additionalRunTime.cost}
          currency={currency}
          details={additionalRunTime.name}
        />
      )}

      {unscheduledStops && (
        <ExtraCard
          title="Additional Unscheduled Stops"
          cost={unscheduledStops.cost}
          currency={currency}
          details={`${unscheduledStops.name} unscheduled ${unscheduledStops.name === "1" ? "stop" : "stops"}`}
        />
      )}

      {tollCharges && (
        <ExtraCard
          title="Toll Charges"
          cost={tollCharges.cost}
          currency={currency}
          details={`${extraTypeOptions.find((option) => option.value === tollCharges.type)?.name} Payment`}
        />
      )}

      {otherExtras && otherExtras.length > 0 && (
        <>
          <Typography variant="title" className="!mt-8 block">
            Other Charges
          </Typography>

          {otherExtras.map((extra, idx) => (
            <ExtraCard
              key={extra.name}
              title={`Charges Description #${idx + 1}: ${extra.name}`}
              cost={extra.cost}
              currency={currency}
              details={`${extraTypeOptions.find((option) => option.value === extra.type)?.name} Payment`}
            />
          ))}
        </>
      )}

      <>
        <Typography variant="title" className="!mt-8 block">
          Notes
        </Typography>

        {providerNotes && (
          <Card className="space-y-3">
            <Typography variant="paragraph2" className="text-neutral-dark-gray">
              {providerNotes}
            </Typography>
            <Typography variant="small-regular" className="text-neutral-dark-gray">
              Note: This notes is to be shared with the driver
            </Typography>
          </Card>
        )}

        {clientNotes && (
          <Card className="space-y-3">
            <Typography variant="paragraph2" className="text-neutral-dark-gray">
              Notes to Customer
            </Typography>
            <Typography>{clientNotes}</Typography>
          </Card>
        )}

        {driver && closeMeta.driverNotes && (
          <Card className="p-0">
            <Blockquote avatarURL={getAssetUrl(driver?.avatar)} heading={driver.name}>
              {closeMeta.driverNotes}
            </Blockquote>
          </Card>
        )}
      </>
    </div>
  );
};
